// videoStreams/videoStream.provider.tsx

import { createContext, useContext, useState } from "react";
import { toast } from "react-toastify";

import api from "../../services";
import { useUser } from "../users";
import { useLocation, useNavigate } from "react-router-dom";
import { IProviderProps } from "../tenant/tenant.interfaces";
import {
  ICreateVideoStream,
  IUpdateVideoStream,
  IVideoStream,
  IUploadUrlRequest,
  IUploadUrlResponse,
  IPlaybackUrlResponse,
} from "./videoStreams.interface";

export const VideoStreamContext = createContext<any | null>(null);

export const VideoStreamProvider = ({ children }: IProviderProps) => {
  const [allVideoStreams, setAllVideoStreams] = useState<IVideoStream[] | null>(
    null
  );
  const [currentVideoStream, setCurrentVideoStream] =
    useState<IVideoStream | null>(null);
  const navigate = useNavigate();
  const { token, setApiLoading, setPageMounting } = useUser();
  const location = useLocation();
  const path = location.pathname.split("/")[1];

  // Buscar todos os vídeos
  const getAllVideoStreams = (filterActive = true) => {
    setPageMounting(true);
    const queryParams = filterActive ? "?active=true&checkStatus=true" : "?checkStatus=true";

    api
      .get(`/video-streams${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setAllVideoStreams(res.data.items || res.data);
        setPageMounting(false);
      })
      .catch((err) => {
        setPageMounting(false);
        console.log(
          err.response?.data?.message || "Error fetching video streams"
        );
        toast.error("Failed to load videos");
      });
  };

  // Buscar um vídeo específico por ID
  const getVideoStreamById = async (id: string) => {
    setApiLoading(true);

    try {
      const response = await api.get(`/video-streams/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCurrentVideoStream(response.data);
      setApiLoading(false);
      return response.data;
    } catch (err: any) {
      setApiLoading(false);
      console.log(err.response?.data?.message || "Error fetching video");
      toast.error("Failed to load video details");
      return null;
    }
  };

  // Criar entrada para um vídeo (sem upload)
  const createVideoStream = async (data: ICreateVideoStream) => {
    setApiLoading(true);

    try {
      const response = await api.post("/video-streams", data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      getAllVideoStreams();
      setApiLoading(false);
      navigate(`/${path}/video-streams/${response.data.id}`);
      return response.data;
    } catch (err: any) {
      setApiLoading(false);
      console.log(err.response?.data?.message || "Error creating video");
      toast.error("Failed to create video");
      return null;
    }
  };

  // Obter URL para upload direto ao Cloudflare
  const getUploadUrl = async (
    data: IUploadUrlRequest
  ): Promise<IUploadUrlResponse | null> => {
    setApiLoading(true);

    try {
      // Se há uma sourceUrl, estamos lidando com um upload via URL
      if (data.sourceUrl) {
        // Chamar um endpoint específico para upload via URL ou adicionar a URL ao payload normal
        const response = await api.post("/video-streams/upload-url", data, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setApiLoading(false);
        return response.data;
      } else {
        // Upload normal via arquivo
        const response = await api.post("/video-streams/upload-url", data, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setApiLoading(false);
        return response.data;
      }
    } catch (err: any) {
      setApiLoading(false);
      console.log(err.response?.data?.message || "Error getting upload URL");
      toast.error("Failed to generate upload link");
      return null;
    }
  };

  // Upload do arquivo para Cloudflare usando a URL direta
  const uploadVideoFile = async (
    uploadURL: string,
    file: File
  ): Promise<boolean> => {
    setApiLoading(true);

    try {
      // Upload direto para o Cloudflare (não passa pelo nosso backend)
      await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      setApiLoading(false);
      toast.success(
        "Video uploaded successfully. Processing will begin shortly."
      );
      return true;
    } catch (err: any) {
      setApiLoading(false);
      console.log(err.message || "Error uploading video");
      toast.error("Failed to upload video");
      return false;
    }
  };

  // Atualizar informações de um vídeo
  const updateVideoStream = async (id: string, data: IUpdateVideoStream) => {
    setApiLoading(true);

    try {
      const response = await api.patch(`/video-streams/${id}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCurrentVideoStream(response.data);
      getAllVideoStreams();
      setApiLoading(false);
      toast.success("Video updated successfully");
      return response.data;
    } catch (err: any) {
      setApiLoading(false);
      console.log(err.response?.data?.message || "Error updating video");
      toast.error("Failed to update video");
      return null;
    }
  };

  // Remover um vídeo (soft delete por padrão)
  const deleteVideoStream = async (id: string, hardDelete = true) => {
    setApiLoading(true);
    const queryParam = hardDelete ? "?hardDelete=true" : "";

    try {
      await api.delete(`/video-streams/${id}${queryParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      getAllVideoStreams();
      setApiLoading(false);
      navigate(`/${path}/video-streams`);
      toast.success(hardDelete ? "Video permanently deleted" : "Video removed");
      return true;
    } catch (err: any) {
      setApiLoading(false);
      console.log(err.response?.data?.message || "Error deleting video");
      toast.error("Failed to delete video");
      return false;
    }
  };

  // Obter URL de reprodução com token assinado
  const getPlaybackUrl = async (
    id: string
  ): Promise<IPlaybackUrlResponse | null> => {
    try {
      const response = await api.get(`/video-streams/${id}/playback-url`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (err: any) {
      console.log(err.response?.data?.message || "Error getting playback URL");
      toast.error("Failed to get video playback link");
      return null;
    }
  };

  // Função de utilidade para verificar status de processamento
  const checkVideoProcessingStatus = async (id: string): Promise<string> => {
    try {
      const video = await getVideoStreamById(id);
      return video?.status || "unknown";
    } catch (err) {
      console.error("Error checking video status:", err);
      return "error";
    }
  };

  return (
    <VideoStreamContext.Provider
      value={{
        allVideoStreams,
        currentVideoStream,
        setCurrentVideoStream,
        getAllVideoStreams,
        getVideoStreamById,
        createVideoStream,
        getUploadUrl,
        uploadVideoFile,
        updateVideoStream,
        deleteVideoStream,
        getPlaybackUrl,
        checkVideoProcessingStatus,
      }}
    >
      {children}
    </VideoStreamContext.Provider>
  );
};

export const useVideoStream = () => useContext(VideoStreamContext);
